import { useEffect, useState } from "react";
import {
  Calendar,
  Loader2,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  Trophy,
  X,
} from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

const OUTCOMES = [
  {
    outcome: "connected",
    label: "Connected — interested",
    Icon: PhoneCall,
    tone: "emerald",
  },
  { outcome: "no_answer", label: "No answer", Icon: PhoneMissed, tone: "slate" },
  { outcome: "busy", label: "Busy / engaged", Icon: PhoneOff, tone: "amber" },
  { outcome: "wrong_number", label: "Wrong number", Icon: X, tone: "rose" },
  {
    outcome: "not_interested",
    label: "Not interested",
    Icon: X,
    tone: "rose",
  },
  {
    outcome: "callback_scheduled",
    label: "Schedule a callback",
    Icon: Calendar,
    tone: "indigo",
  },
  { outcome: "closed_won", label: "Closed · Won", Icon: Trophy, tone: "amber" },
];

const TONE = {
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
  slate: "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100",
  amber: "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100",
  rose: "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100",
};

/**
 * Disposition modal — captures the outcome of an inline phone call.
 * Reuses POST /admin/dialer/disposition (same backend as Power Dialler).
 * Props:
 *  - lead: the lead being dispositioned (null = closed)
 *  - onClose: () => void
 *  - onLogged: (updatedLead) => void   // updated lead returned from server
 */
export default function DispositionModal({ lead, onClose, onLogged }) {
  const [outcome, setOutcome] = useState(null);
  const [notes, setNotes] = useState("");
  const [callbackAt, setCallbackAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset internal state whenever the lead changes
  useEffect(() => {
    setOutcome(null);
    setNotes("");
    setCallbackAt("");
    setSubmitting(false);
  }, [lead?.id]);

  if (!lead) return null;

  const submit = async () => {
    if (!outcome) {
      toast.error("Pick an outcome");
      return;
    }
    const extra = {};
    if (outcome === "callback_scheduled") {
      if (!callbackAt) {
        toast.error("Pick a callback time");
        return;
      }
      extra.follow_up_at = new Date(callbackAt).toISOString();
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/dialer/disposition", {
        lead_id: lead.id,
        outcome,
        notes: notes.trim() || null,
        ...extra,
      });
      toast.success("Call logged ✓", { duration: 1800 });
      onLogged?.(data.lead);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const skip = () => {
    toast("Call attempt kept — no outcome logged", { duration: 1500 });
    onClose?.();
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-md"
        data-testid="disposition-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight text-[#0A2540]">
            How did the call go?
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-[#0A2540]">{lead.name}</span> ·{" "}
            <span className="font-mono">{lead.phone}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 pt-2">
          {OUTCOMES.map((o) => {
            const active = outcome === o.outcome;
            const Icon = o.Icon;
            return (
              <button
                key={o.outcome}
                type="button"
                onClick={() => setOutcome(o.outcome)}
                className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  TONE[o.tone] || TONE.slate
                } ${
                  active ? "ring-2 ring-offset-1 ring-[#0A2540]" : ""
                }`}
                data-testid={`dispo-${o.outcome}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
                <span className="leading-tight">{o.label}</span>
              </button>
            );
          })}
        </div>

        {outcome === "callback_scheduled" && (
          <div className="mt-3">
            <Label htmlFor="cbat" className="text-xs text-slate-500">
              Callback time
            </Label>
            <Input
              id="cbat"
              type="datetime-local"
              value={callbackAt}
              onChange={(e) => setCallbackAt(e.target.value)}
              className="mt-1 h-10"
              data-testid="dispo-callback-at"
            />
          </div>
        )}

        <div className="mt-3">
          <Label htmlFor="notes" className="text-xs text-slate-500">
            Notes (optional)
          </Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What did the lead say? Any next steps?"
            className="mt-1 resize-none"
            rows={3}
            data-testid="dispo-notes"
          />
        </div>

        <DialogFooter className="mt-2 flex-row justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={skip}
            className="text-slate-500"
            data-testid="dispo-skip"
          >
            Skip logging
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            data-testid="dispo-save"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              "Save & close"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
